import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  TagsEndpoints as Endpoints,
  type GetAllTagsRequest,
  type GetAllTagsResponse,
  type GetClientTagsRequest,
  type GetClientTagsResponse,
  type GetListTagsRequest,
  type GetListTagsResponse,
  type GetTagByIdRequest,
  type GetTagByIdResponse,
  type GetTagsRequest,
  type GetTagsResponse,
  type RemoveClientsTagsRequest,
  type RemoveClientsTagsResponse,
  type SetClientsTagsBySearchIdRequest,
  type SetClientsTagsBySearchIdResponse,
  type SetClientsTagsRequest,
  type SetClientsTagsResponse,
  type UpdateClientTagsRequest,
  type UpdateClientTagsResponse,
  type UpdateTagRequest,
  type UpdateTagResponse,
} from '@app/api/Tags/types';
import { TAGS_CHUNK } from '@app/constants';

const TAGS_LIST_TAG = 'TagsList';

export const tagsApi = createApi({
  reducerPath: 'tagsApi2',
  baseQuery: createApiBaseQuery(TAGS_CHUNK),
  tagTypes: [TAGS_LIST_TAG],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetTagById]: query<GetTagByIdResponse, GetTagByIdRequest>({
      query: (params) => ({
        url: Endpoints.GetTagById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.UpdateTag]: mutation<UpdateTagResponse, UpdateTagRequest>({
      query: ({ data, logs, ...params }) => ({
        url: Endpoints.UpdateTag,
        method: Methods.POST,
        params,
        data,
        logs,
      }),
      invalidatesTags: [TAGS_LIST_TAG],
    }),
    [Endpoints.GetTags]: query<GetTagsResponse, GetTagsRequest>({
      query: (params) => ({
        url: Endpoints.GetTags,
        method: Methods.GET,
        params,
      }),
      providesTags: [TAGS_LIST_TAG],
    }),
    [Endpoints.GetClientTags]: query<
      GetClientTagsResponse,
      GetClientTagsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientTags,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetClientsTags]: mutation<
      SetClientsTagsResponse,
      SetClientsTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetClientsTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.RemoveClientsTags]: mutation<
      RemoveClientsTagsResponse,
      RemoveClientsTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.RemoveClientsTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetClientsTagsBySearchId]: mutation<
      SetClientsTagsBySearchIdResponse,
      SetClientsTagsBySearchIdRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetClientsTagsBySearchId,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateClientTags]: mutation<
      UpdateClientTagsResponse,
      UpdateClientTagsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateClientTags,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetListTags]: query<GetListTagsResponse, GetListTagsRequest>({
      query: (params) => ({
        url: Endpoints.GetListTags,
        method: Methods.GET,
        params,
      }),
      providesTags: [TAGS_LIST_TAG],
    }),
    [Endpoints.GetAllTags]: query<GetAllTagsResponse, GetAllTagsRequest>({
      query: (params) => ({
        url: Endpoints.GetAllTags,
        method: Methods.GET,
        params,
      }),
      providesTags: [TAGS_LIST_TAG],
    }),
  }),
});

export const {
  useGetTagByIdQuery,
  useLazyGetTagByIdQuery,
  useUpdateTagMutation,
  useGetTagsQuery,
  useLazyGetTagsQuery,
  useGetClientTagsQuery,
  useLazyGetClientTagsQuery,
  useSetClientsTagsMutation,
  useRemoveClientsTagsMutation,
  useSetClientsTagsBySearchIdMutation,
  useUpdateClientTagsMutation,
  useGetListTagsQuery,
  useLazyGetListTagsQuery,
  useGetAllTagsQuery,
  useLazyGetAllTagsQuery,
} = tagsApi;
